/**
 * ModernWash read-only legacy adapter API.
 *
 * 통합 Mogoo 관리자에서 세차 영역을 채우는 read-only 어댑터 엔드포인트다.
 * legacy 데이터를 mogoo_production 으로 복사/이관하지 않고, backend 어댑터가
 * 기존 ModernWash v1 API 를 HTTP 로 읽어 안정 DTO 로 변환해 내려준다.
 * (계약: docs/product/modernwash-legacy-adapter.md, ADR-005)
 *
 * 기존 관리자 api client 재사용 (토큰 자동 첨부 + 401 시 /login 리다이렉트).
 *
 * 스코프 규칙 — members/academy API 와 동일:
 *   master  는 현재 선택한 storeId 를 같이 보낸다 (getScopedParams).
 *   manager 는 storeId 를 보내지 않는다 — backend 가 본인 매장으로 강제한다.
 *
 * 모든 응답은 공통 봉투를 갖는다:
 *   { enabled, scope:{ role, storeId, storeName, linkedToModernwash },
 *     source:{ reachable, degraded, fetchedAt, error }, data | items }
 * 봉투 해석/표시는 modules/carwash/modernwash/sourceState.js 가 담당한다.
 */
import api from '../../api/client';

/** 소스 상태 — 어댑터 self + v1 /health 도달 여부. data 봉투. */
export async function fetchModernwashStatus(params = {}) {
  const res = await api.get('/owner-console/modernwash/status', { params });
  return res.data;
}

/**
 * ModernWash V1 미러의 마지막 성공 수신 상태.
 *
 * legacy 미러는 읽기 전용 운영 참고 데이터이므로, 이 값은 "지금 원천에서 직접
 * 조회한 시각"이 아니라 마지막으로 검증된 수신 시각을 뜻한다.
 */
export async function fetchModernwashMirrorFreshness(params = {}) {
  const res = await api.get('/owner-console/modernwash/mirror/freshness', { params });
  return res.data;
}

/**
 * 전주점 원천 귀속/예외 점검용 선택적 API.
 *
 * 백엔드 전환 단계에서도 콘솔이 깨지지 않도록, 아직 배포되지 않은 404는
 * "준비 중" 결과로 바꾼다. 그 밖의 인증·연결 오류는 숨기지 않고 호출부에
 * 전달한다. 이 API는 조회 전용이며, 귀속 처리나 데이터 변경을 하지 않는다.
 */
export async function fetchModernwashDataQuality(params = {}) {
  try {
    const res = await api.get('/owner-console/modernwash/data-quality', { params });
    return res.data;
  } catch (error) {
    if (error?.response?.status === 404) {
      return {
        enabled: true,
        data: {
          available: false,
          unavailableReason: '원천 귀속 점검 API가 아직 배포되지 않았어요.',
        },
      };
    }
    throw error;
  }
}

/**
 * 전주점 세차 보관본 조회.
 *
 * 이 두 API는 Mogoo MongoDB에 보관된 jj001 과거 집계값만 읽어 보여준다.
 * 외주 jj001 서버를 화면 요청마다 직접 조회하지 않는다. 보관 수집 API가 아직
 * 배포되지 않았거나 보관본이 없을 때의 404/503은 화면이 깨지지 않도록
 * `available:false`로 정규화한다. 그 밖의 오류(권한, 네트워크 등)는 숨기지 않고
 * 호출 화면에 전달한다.
 */
const unavailableJj001WashReport = (reason) => ({
  available: false,
  unavailableReason: reason || '아직 모구 보관본을 수집하지 않았습니다.',
});

export async function fetchJeonjuWashReportDaily(params = {}) {
  try {
    const res = await api.get('/owner-console/jeonju-wash-reports/daily', { params });
    return res.data;
  } catch (error) {
    if ([404, 503].includes(error?.response?.status)) {
      return unavailableJj001WashReport(
        error?.response?.data?.message || '아직 모구 보관본을 수집하지 않았습니다.',
      );
    }
    throw error;
  }
}

export async function fetchJeonjuWashReportRange(params = {}) {
  try {
    const res = await api.get('/owner-console/jeonju-wash-reports/range', { params });
    return res.data;
  } catch (error) {
    if ([404, 503].includes(error?.response?.status)) {
      return unavailableJj001WashReport(
        error?.response?.data?.message || '아직 모구 보관본을 수집하지 않았습니다.',
      );
    }
    throw error;
  }
}

/** 차량 목록 — v1 /member/admin/members(/:id/vehicles). items 봉투. */
export async function fetchModernwashVehicles(params = {}) {
  const res = await api.get('/owner-console/modernwash/vehicles', { params });
  return res.data;
}

/** LPR 인식 로그 — v1 /kiosk/lpr-logs. items 봉투. */
export async function fetchModernwashLprLogs(params = {}) {
  const res = await api.get('/owner-console/modernwash/lpr-logs', { params });
  return res.data;
}

/** 키오스크 상태 — v1 /kiosk/maintenance + /kiosk/admin/payments/summary. data 봉투. */
export async function fetchModernwashKiosk(params = {}) {
  const res = await api.get('/owner-console/modernwash/kiosk', { params });
  return res.data;
}

/** 세차 프로그램(코스) — v1 /kiosk/admin/courses. items 봉투. */
export async function fetchModernwashCourses(params = {}) {
  const res = await api.get('/owner-console/modernwash/courses', { params });
  return res.data;
}

/** RF 카드 — 원천 확인(없으면 SOURCE_NO_DATA). items 봉투. */
export async function fetchModernwashRfCards(params = {}) {
  const res = await api.get('/owner-console/modernwash/rf-cards', { params });
  return res.data;
}

/** 기존 ModernWash 회원 구독 목록 — 지점 운영 콘솔에서 읽기 전용으로 표시한다. */
export async function fetchModernwashSubscriptions(params = {}) {
  const res = await api.get('/owner-console/modernwash/subscriptions', { params });
  return res.data;
}

/** 기존 ModernWash 매출 원장 — 정산 설정 전에는 조회·내보내기 판단용으로만 쓴다. */
export async function fetchModernwashSales(params = {}) {
  const res = await api.get('/owner-console/modernwash/sales', { params });
  return res.data;
}
